import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Auto-hide the mobile top header + bottom nav while the user is reading.
 *
 * Behaviour (mirrors iOS Safari / Instagram / TikTok):
 *   - Down by ≥ DOWN_THRESHOLD px → hide.
 *   - Up   by ≥ UP_THRESHOLD   px → show.
 *   - At scrollTop ≤ SHOW_AT_TOP_PX → always show.
 *   - At end-of-page (last EOP_BUFFER px) → always show.
 *   - 1.5 s of no scrolling → snap back to show.
 *   - Input/textarea/contenteditable focused → always show.
 *   - Any `[role=dialog]`, `[data-zen-overlay]`, or `[aria-modal=true]` in
 *     the DOM → always show (don't yank chrome away under a modal).
 *
 * Disabled when ANY of these is true (= chrome forced visible):
 *   - viewport ≥ 768 px (tablet / desktop)
 *   - pathname starts with `/auth`, `/onboarding`, or `/settings`
 *   - `window.__zenscrollForceChrome = true` (manual override / QA)
 *
 * Mechanism — no React state, no Context, no prop drilling:
 *   - Writes `data-chrome="visible" | "hidden"` to <html>.
 *   - Writes `data-chrome-reason="<reason>"` for debugging.
 *   - CSS in `index.css` translates any `.mobile-chrome-top` /
 *     `.mobile-chrome-bottom` element via `transform` (zero reflow).
 *
 * Escape hatch:
 *   `window.__zenscrollShowChrome()` forces visible immediately.
 */

const DOWN_THRESHOLD = 48;
const UP_THRESHOLD = 16;
const NOISE_FLOOR_PX = 4;
const SHOW_AT_TOP_PX = 8;
const EOP_BUFFER = 80;
const MOBILE_MAX_WIDTH = 767; // inclusive — same as the CSS @media query
const EXCLUDED_PREFIXES = ['/auth', '/onboarding', '/settings'];

const isExcludedPath = (pathname) =>
  EXCLUDED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`),
  );

const isOverlayOpen = () => {
  // Match common overlay/modal/drawer patterns:
  //   - radix-ui / shadcn renders `[role=dialog][data-state=open]`
  //   - generic ARIA modals
  //   - our own onboarding/profile sheet uses `[data-zen-overlay]`
  return !!document.querySelector(
    '[role="dialog"][data-state="open"], [aria-modal="true"], [data-zen-overlay="open"]',
  );
};

const isInputFocused = () => {
  const ae = document.activeElement;
  if (!ae) return false;
  if (/^(INPUT|TEXTAREA|SELECT)$/i.test(ae.tagName)) return true;
  if (ae.isContentEditable) return true;
  return false;
};

export function useMobileChromeAutoHide() {
  const { pathname } = useLocation();
  const lastYRef = useRef(0);
  const accDownRef = useRef(0);
  const accUpRef = useRef(0);
  const rafScheduledRef = useRef(false);

  useEffect(() => {
    const html = document.documentElement;

    const setVis = (visible, reason) => {
      const next = visible ? 'visible' : 'hidden';
      if (html.dataset.chrome !== next) html.dataset.chrome = next;
      if (reason) html.dataset.chromeReason = reason;
    };
    const forceShow = (reason) => setVis(true, reason);

    // Expose a manual escape hatch (handy for QA, `chrome://inspect`, or
    // any place a future modal forgets to mark itself as an overlay).
    window.__zenscrollShowChrome = () => forceShow('manual-force-show');

    // Helper: are we currently allowed to auto-hide?  Re-evaluated on
    // every scroll tick AND on every resize / orientation change so the
    // hook self-corrects when the viewport crosses 768 px.
    const isDisabled = () =>
      window.innerWidth > MOBILE_MAX_WIDTH ||
      isExcludedPath(pathname) ||
      window.__zenscrollForceChrome === true;

    // Initial state.  Always start visible.
    forceShow('init');
    lastYRef.current = 0;
    accDownRef.current = 0;
    accUpRef.current = 0;

    const getY = () =>
      document.body.scrollTop || document.documentElement.scrollTop || window.scrollY || 0;

    const isAtBottom = () => {
      const scrollH = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      const viewportH = window.innerHeight;
      return getY() + viewportH >= scrollH - EOP_BUFFER;
    };

    const update = () => {
      rafScheduledRef.current = false;

      if (isDisabled()) {
        forceShow('disabled');
        return;
      }

      const y = getY();

      // Guard rail #1 — pinned at the top.
      if (y <= SHOW_AT_TOP_PX) {
        accDownRef.current = 0;
        accUpRef.current = 0;
        lastYRef.current = y;
        forceShow('at-top');
        return;
      }
      // Guard rail #2 — end-of-page.  No more "down" to scroll, no reason to hide.
      if (isAtBottom()) {
        forceShow('at-bottom');
        return;
      }
      // Guard rail #3 — input focused (keyboard open).
      if (isInputFocused()) {
        forceShow('input-focus');
        return;
      }
      // Guard rail #4 — overlay/modal/drawer open.
      if (isOverlayOpen()) {
        forceShow('overlay-open');
        return;
      }

      const delta = y - lastYRef.current;
      // Noise floor — ignore tiny rubber-band / sub-pixel deltas that
      // come from iOS WKWebView rounding and Android overscroll glow.
      if (Math.abs(delta) < NOISE_FLOOR_PX) return;
      lastYRef.current = y;

      if (delta > 0) {
        accDownRef.current += delta;
        accUpRef.current = 0;
        if (accDownRef.current >= DOWN_THRESHOLD) setVis(false, 'scroll-down');
      } else if (delta < 0) {
        accUpRef.current += -delta;
        accDownRef.current = 0;
        if (accUpRef.current >= UP_THRESHOLD) setVis(true, 'scroll-up');
      }

      // Intentionally NO idle-restore.  iOS Safari behaviour — chrome
      // stays hidden until the user actively scrolls up.  Removes the
      // flicker the user reported when sitting on a long post.
    };

    const onScroll = () => {
      if (rafScheduledRef.current) return;
      rafScheduledRef.current = true;
      requestAnimationFrame(update);
    };

    const onResize = () => {
      // Viewport crossed the mobile breakpoint, or rotated.  Re-evaluate.
      if (isDisabled()) forceShow('resize-disabled');
    };

    // If the hook is currently disabled (desktop, excluded route), still
    // listen for resize so we re-enable when the user shrinks the window.
    // No scroll listeners attached in that branch — saves cycles.
    if (isDisabled()) {
      forceShow('mount-disabled');
      window.addEventListener('resize', onResize, { passive: true });
      return () => {
        window.removeEventListener('resize', onResize);
        forceShow('cleanup');
      };
    }

    document.body.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    return () => {
      document.body.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      // Restore chrome whenever we unmount / route-change so the next
      // page never inherits a stale `hidden` state.
      forceShow('cleanup');
    };
  }, [pathname]);
}
